import { ReactComponent as CultivationKeyIcon } from '@app/assets/cultivation-key.svg';
import { ReactComponent as KarmaIcon } from '@app/assets/karma.svg';
import { ReactComponent as QuestionIcon } from '@app/assets/question.svg';
import QueryProgress from '@app/components/query.progress';
import Tooltip from '@app/domains/common/components/Tooltip';
import CancelButton from '@app/domains/common/components/button/CancelButton';
import GradationButton from '@app/domains/common/components/button/GradationButton';
import type { ClassNameType } from '@app/domains/common/types';
import { ROUTE_PATH_MAP } from '@app/domains/common/utils/path';
import clsx from 'clsx';
import type { ButtonHTMLAttributes, ChangeEvent, HTMLAttributes, PropsWithChildren } from 'react';
import { Link, type LinkProps, useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

function Paywall({ children, ...props }: PropsWithChildren<HTMLAttributes<HTMLElement>>) {
    return (
        <section
            className="shadow-chapter-lock ml-[-20px] mr-[-20px] flex flex-col items-center rounded-[28px] bg-gray-50 px-24 pt-52 pb-[40px] dark:bg-gray-850 dark:shadow-ww-chapter-lock-dark sm:mx-auto sm:w-[375px]"
            {...props}
        >
            {children}
        </section>
    );
}

const ClockIcon = () => {
    return (
        <div className="relative flex justify-center">
            <div className="absolute bottom-0 w-80">
                <img
                    width={80}
                    height={80}
                    src="/images/clock@3x.png"
                    alt="clock with circular arrow indicating time progression"
                />
            </div>
        </div>
    );
};

const LockIcon = () => {
    return (
        <div className="relative flex justify-center">
            <div className="absolute bottom-0 w-80">
                <img
                    width={80}
                    height={80}
                    src="/images/lock-3d@3x.png"
                    alt="locked lock representing last hold back"
                />
            </div>
        </div>
    );
};

const Title = ({ title }: { title: string }) => {
    return <h2 className="font-set-sb20 text-gray-t1">{title}</h2>;
};

const Desc = ({ className, text, ...props }: { text: string } & HTMLAttributes<HTMLParagraphElement>) => {
    return (
        <p className={twMerge('font-set-sb16 pt-4 text-center text-gray-desc', className)} role="status" {...props}>
            {text}
        </p>
    );
};

const Or = () => {
    return (
        <div className="my-12 flex items-center justify-center">
            <div className="h-1 w-81 bg-gray-line-base" />
            <span className="font-set-m14 px-12 text-gray-placehold">or</span>
            <div className="h-1 w-81 bg-gray-line-base" />
        </div>
    );
};

const KarmaPayButton = ({
    isLoading,
    karmaPrice,
    ...buttonProps
}: {
    isLoading: boolean;
    disabled: boolean;
    karmaPrice: number;
    onClick?: () => void;
} & ButtonHTMLAttributes<HTMLButtonElement>) => {
    return (
        <GradationButton className="h-52 w-260 rounded-full !shadow-none" {...buttonProps}>
            {isLoading ? (
                <QueryProgress
                    classes={{ wrapper: 'my-0', progress: { root: 'text-white/70' } }}
                    status="loading"
                    size={32}
                />
            ) : (
                <div className="font-set-b16 flex items-center text-white">
                    <span>BUY AND READ</span>
                    <span className="ml-8">
                        <KarmaIcon className="h-16 w-16" />
                    </span>
                    <span className="ml-4" data-testid="karma-price">
                        {karmaPrice}
                    </span>
                </div>
            )}
        </GradationButton>
    );
};

const VoucherPayButton = ({
    isLoading,
    disabled,
    voucherAmount,
    availableVouchers,
    ...buttonProps
}: {
    isLoading: boolean;
    disabled: boolean;
    voucherAmount: number;
    availableVouchers: number;
    onClick?: () => void;
} & ButtonHTMLAttributes<HTMLButtonElement>) => {
    return (
        <>
            <GradationButton
                className={clsx('h-52 w-260 rounded-full !shadow-none', {
                    'opacity-60': disabled || !availableVouchers,
                })}
                disabled={!availableVouchers}
                {...buttonProps}
            >
                {isLoading ? (
                    <QueryProgress
                        classes={{ wrapper: 'my-0', progress: { root: 'text-white/70' } }}
                        status="loading"
                        size={32}
                    />
                ) : (
                    <div className="font-set-b16 flex items-center text-white">
                        <span>UNLOCK WITH</span>
                        <span className="ml-8">
                            <CultivationKeyIcon className="text-white" />
                        </span>
                        <span className="ml-4" data-testid="voucher-amount">
                            {voucherAmount}
                        </span>
                    </div>
                )}
            </GradationButton>
            {!availableVouchers && <div className="mt-5">No cultivation keys left.</div>}
        </>
    );
};

const AutoUnlock = ({
    defaultChecked,
    onChange,
}: {
    defaultChecked?: boolean;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}) => {
    return (
        <div className="flex items-center">
            <input
                type="checkbox"
                name="auto-unlock"
                id="auto-unlock"
                className="form-checkbox h-22 w-22 rounded-full border-none bg-transparent ring-1 ring-inset ring-gray-t3 checked:bg-[length:80%] checked:!ring-transparent focus:ring-1 focus:ring-inset focus:ring-gray-t3 focus:ring-offset-0"
                defaultChecked={defaultChecked}
                onChange={onChange}
            />
            <label htmlFor="auto-unlock" className="font-set-sb14 ml-4 text-gray-desc">
                Enable Auto Unlock
            </label>
            <Tooltip
                title={
                    'Auto-Unlock is a feature that automatically unlocks chapters without confirmation. Unlock will use WTU, Keys, and Karma, in that order.'
                }
            >
                <div className="ml-4">
                    <QuestionIcon className="h-18 w-18 text-gray-assist" />
                </div>
            </Tooltip>
        </div>
    );
};

const ReadFreeButton = ({ ...props }: ButtonHTMLAttributes<HTMLButtonElement>) => {
    return (
        <GradationButton className="h-52 w-260 rounded-full !shadow-none" {...props}>
            <div className="font-set-b16 text-white">
                <span>READ FOR FREE</span>
            </div>
        </GradationButton>
    );
};

const ChampionSubscribeButton = ({ ...props }: ButtonHTMLAttributes<HTMLButtonElement>) => {
    return (
        <div className="pt-10">
            <button className="h-52 w-260 rounded-full bg-gray-bg-elevate shadow-ww-text-container" {...props}>
                <div className="flex h-full flex-col items-center pt-3">
                    <img
                        className="h-28 w-[122.5px]"
                        width={122.5}
                        height={28}
                        src="/images/champion-logo@3x.png"
                        alt="champion logo"
                    />
                    <p className="font-set-sb12 text-gray-desc">Subscribe for All Chapters</p>
                </div>
            </button>
        </div>
    );
};

const VipFreeNovelEncourage = () => {
    const navigate = useNavigate();

    return (
        <>
            <div className="px-16 pb-8">
                <p className="font-set-sb14 text-center text-gray-desc">
                    You are VIP member! You can choose this novel as your monthly free novel.{' '}
                </p>
            </div>
            <div className="py-10">
                <CancelButton
                    className="h-52 w-260 rounded-full"
                    onClick={() => navigate(ROUTE_PATH_MAP.VIP_SUBSCRIPTION)}
                >
                    <span className="font-set-b16 text-blue-600 dark:text-blue-500">CHOOSE AS A FREE STORY</span>
                </CancelButton>
            </div>
        </>
    );
};

const VipFreeNovelLink = ({ ...props }: Omit<LinkProps, 'to'>) => {
    return (
        <Link
            className="mx-auto flex h-52 w-full max-w-[255px] items-center justify-center rounded-100 border-2 border-blue-600 bg-white dark:border-blue-500 dark:bg-black/20"
            to={ROUTE_PATH_MAP.VIP_SUBSCRIPTION}
            data-testid="vip-freestory-link"
            {...props}
        >
            <span className="font-set-b16 text-blue-600 dark:text-blue-500">CHOOSE AS A FREE STORY</span>
        </Link>
    );
};

const Head = ({ className, children }: PropsWithChildren<ClassNameType>) => {
    return <div className={twMerge('mb-28 text-center', className)}>{children}</div>;
};

Paywall.TopIcon = {
    Clock: ClockIcon,
    Lock: LockIcon,
};
Paywall.Title = Title;
Paywall.Desc = Desc;
Paywall.ReadFreeButton = ReadFreeButton;

Paywall.Or = Or;

Paywall.KarmaPayButton = KarmaPayButton;
Paywall.VoucherPayButton = VoucherPayButton;
Paywall.AutoUnlock = AutoUnlock;
Paywall.ChampionSubscribeButton = ChampionSubscribeButton;
Paywall.VipFreeNovelEncourage = VipFreeNovelEncourage;

Paywall.Head = Head;

Head.Title = Title;

Paywall.VipFreeNovelLink = VipFreeNovelLink;

export default Paywall;
